/**
 * Product template ESM (Phase 10).
 *
 * AI: Entirely written by AI
 * Date: 2026-05-04
 * Model: Claude Opus 4.7
 *
 * [Product]
 */

import { log } from './log.js';
import { appGet } from './app-get.js';
import { getEasing } from './easing.js';
import { fadeOut, stop as stopAnim } from './core-anim.js';
import { dismiss as notifierDismiss } from './notifier.js';

const PRODUCT_SELECTOR = '#product';
const MAIN_IMAGES_SELECTOR = '.main-product-images-large';
const PRODUCT_IMAGE_WRAP_SELECTOR = '.product-image-wrap';
const MORE_IMAGES_SELECTOR = '.js-more-product-images';
const ADD_PANEL_SELECTOR = '.js-add-panel';
const ADD_PANEL_AFTER_SELECTOR = '.js-add-panel__after';
const STICKY_ATC_HIDDEN_CLASS = 'sticky-add-to-cart--hidden';
const NOT_VISIBLE_CLASS = 'not-visible';
const SCROLLED_PAST_LAST_CLASS = 'scrolled-past-last';
const MORE_IMAGES_REVEAL_DELAY = 4000;
const MORE_IMAGES_FADE_MS = 500;
const PAST_LAST_VIEWPORT_RATIO = 1.5;

let activeTeardown = null;

function getStickyAtcSelector() {
  return appGet('dom.product.stickyAtcSelector', '.js-sticky-add-to-cart');
}

function getAddItemFormSelector() {
  return appGet('dom.product.addItemFormSelector', '.js-add-item-form');
}

function getHeaderHeight() {
  const header = document.querySelector('#header-global');
  return header ? header.offsetHeight : 0;
}

function elementInView(el) {
  if (!el) return false;
  const viewportTop = window.pageYOffset + getHeaderHeight();
  const viewportBottom = viewportTop + window.innerHeight;
  const rect = el.getBoundingClientRect();
  const elementTop = rect.top + window.pageYOffset;
  const elementBottom = elementTop + rect.height;
  return elementBottom > viewportTop && elementTop < viewportBottom;
}

function isSmallOrMediumBreakpoint() {
  const bp = appGet('currentBreakpoint', null);
  return bp === 'small' || bp === 'medium';
}

// Sizing modal trigger lives in <sizing-modal-opener> (theme/js/components/sizing-modal-opener.esm.js).

// ─── Sticky ATC ─────────────────────────────────────────────────────────────

function setupStickyAddToCart(product) {
  const stickyAtc = product.querySelector(getStickyAtcSelector());
  if (!stickyAtc) return null;
  const addPanel = product.querySelector(ADD_PANEL_SELECTOR);
  if (!addPanel) return null;

  const formAddPanelAfter = product.querySelector(
    `${getAddItemFormSelector()} ${ADD_PANEL_AFTER_SELECTOR}`
  );
  const stickyAtcAddPanelAfter = stickyAtc.querySelector(ADD_PANEL_AFTER_SELECTOR);
  if (formAddPanelAfter && stickyAtcAddPanelAfter) {
    for (const child of formAddPanelAfter.children) {
      stickyAtcAddPanelAfter.appendChild(child.cloneNode(true));
    }
  }

  // direction === 'in' shows; 'out' hides. The hide path adds the
  // hidden class up-front so handleStickyAddToCart's isHidden guard
  // flips immediately, then removes it once the WAAPI animation
  // settles — preserving the IIFE's reset-for-next-show contract.
  //
  // CSS defaults .sticky-add-to-cart to display: none; the IIFE relied
  // on Velocity's fadeIn/fadeOut to flip display alongside opacity.
  // WAAPI keyframes only animate opacity, so we toggle display
  // explicitly: set 'block' before showing, set 'none' after the hide
  // animation settles. (Mirrors the gotcha called out in CLAUDE.md.)
  function animateStickyAtc(direction) {
    const props = appGet('animations.stickyAtc', null);
    if (!props || !props[direction]) return;
    const hide = direction === 'out';
    stickyAtc.classList.toggle(STICKY_ATC_HIDDEN_CLASS, hide);
    stopAnim(stickyAtc);
    if (!hide) stickyAtc.style.display = 'block';
    const anim = stickyAtc.animate(props[direction].keyframes, {
      duration: props[direction].duration,
      easing: getEasing(),
      fill: 'forwards',
    });
    if (hide) {
      anim.finished.then(() => {
        stickyAtc.classList.remove(STICKY_ATC_HIDDEN_CLASS);
        stickyAtc.style.display = 'none';
      });
    }
  }

  function handleStickyAddToCart() {
    if (!isSmallOrMediumBreakpoint()) return;
    const isHidden =
      getComputedStyle(stickyAtc).display === 'none' ||
      stickyAtc.classList.contains(STICKY_ATC_HIDDEN_CLASS);
    const inView = elementInView(addPanel);
    if (inView && !isHidden) animateStickyAtc('out');
    else if (!inView && isHidden) animateStickyAtc('in');
  }

  // On scroll only — not at load: a bar shown before the visitor moves covers
  // whatever sits at the bottom of a phone viewport (the hemming row's "More
  // info" link, which axe's target-size then reports as obscured). The first
  // scroll brings it in if the add panel is still out of view.
  window.addEventListener('scroll', handleStickyAddToCart, { passive: true });

  return function teardownStickyAtc() {
    window.removeEventListener('scroll', handleStickyAddToCart);
  };
}

// ─── Stacked image gallery (Phase 7 rAF) ────────────────────────────────────

function setupProductImages(product) {
  const mainImages = document.querySelector(MAIN_IMAGES_SELECTOR);
  if (!mainImages) return null;
  const productImages = Array.from(mainImages.querySelectorAll(PRODUCT_IMAGE_WRAP_SELECTOR));
  if (productImages.length === 0) return null;

  const moreImages = document.querySelector(MORE_IMAGES_SELECTOR);
  let rafId = 0;
  let revealTimer = null;
  let moreImagesClickHandler = null;
  let moreImagesScrollHandler = null;

  productImages.forEach((image, index) => {
    image.style.zIndex = String(productImages.length - index);
  });

  // Scroll-invariant geometry: read on setup + on resize, never on the
  // rAF hot path. Avoids two forced layouts per scroll frame.
  let sceneDuration = 0;
  let mainTop = 0;

  function measure() {
    sceneDuration = productImages[0].getBoundingClientRect().height;
    mainTop = mainImages.getBoundingClientRect().top + window.pageYOffset;
  }

  function setMainHeight() {
    mainImages.style.height = sceneDuration * (productImages.length + 1) + 'px';
  }

  function updateImageVisibility() {
    if (sceneDuration === 0) return;
    const progress = window.pageYOffset - mainTop;
    const numImages = productImages.length;
    const isPastLast = progress >= sceneDuration * numImages;

    mainImages.classList.toggle(SCROLLED_PAST_LAST_CLASS, isPastLast);

    if (isPastLast || progress < 0) {
      productImages.forEach((image) => image.classList.remove(NOT_VISIBLE_CLASS));
      return;
    }
    const activeIndex = Math.floor(progress / sceneDuration);
    productImages.forEach((image, index) => {
      image.classList.toggle(NOT_VISIBLE_CLASS, index < activeIndex);
    });
  }

  function onProductScroll() {
    cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(updateImageVisibility);
  }

  function recalculateDurations() {
    measure();
    setMainHeight();
    updateImageVisibility();
  }

  measure();
  setMainHeight();

  if (appGet('breakpoints.largeUp', { matches: false }).matches && moreImages) {
    revealTimer = setTimeout(() => {
      moreImages.style.display = 'block';
      moreImages.animate(
        [
          { transform: 'translateY(100%)', opacity: 0 },
          { transform: 'translateY(0)', opacity: 1 },
        ],
        { duration: MORE_IMAGES_FADE_MS, easing: getEasing(), fill: 'forwards' }
      );

      moreImagesClickHandler = (event) => {
        event.preventDefault();
        window.scrollTo({
          top: window.pageYOffset + window.innerHeight,
          behavior: 'smooth',
        });
        fadeOut(moreImages, { duration: MORE_IMAGES_FADE_MS });
      };
      moreImages.addEventListener('click', moreImagesClickHandler);
    }, MORE_IMAGES_REVEAL_DELAY);

    moreImagesScrollHandler = () => {
      if (window.pageYOffset > window.innerHeight * PAST_LAST_VIEWPORT_RATIO) {
        if (moreImages.offsetParent !== null) {
          fadeOut(moreImages, { duration: MORE_IMAGES_FADE_MS });
        } else if (revealTimer) {
          clearTimeout(revealTimer);
          revealTimer = null;
        }
      }
    };
    window.addEventListener('scroll', moreImagesScrollHandler, { passive: true });
  }

  window.addEventListener('resize', recalculateDurations);
  window.addEventListener('scroll', onProductScroll, { passive: true });
  updateImageVisibility();

  return function teardownImages() {
    cancelAnimationFrame(rafId);
    if (revealTimer) clearTimeout(revealTimer);
    window.removeEventListener('resize', recalculateDurations);
    window.removeEventListener('scroll', onProductScroll);
    if (moreImagesScrollHandler) {
      window.removeEventListener('scroll', moreImagesScrollHandler);
    }
    if (moreImages && moreImagesClickHandler) {
      moreImages.removeEventListener('click', moreImagesClickHandler);
    }
  };
}

// ─── Page-change orchestration ──────────────────────────────────────────────

function teardown() {
  if (typeof activeTeardown === 'function') {
    activeTeardown();
  }
  activeTeardown = null;
}

function onPageChange() {
  const product = document.querySelector(PRODUCT_SELECTOR);
  if (product) {
    teardown();
    const teardownImages = setupProductImages(product);
    const teardownSticky = setupStickyAddToCart(product);
    activeTeardown = () => {
      if (teardownImages) teardownImages();
      if (teardownSticky) teardownSticky();
    };
    return;
  }
  // Left a product page — clean up sticky scroll listener and any open
  // sizing/hemming modal carried over from the previous product view.
  const modalActive = appGet('isModalActive', false);
  const modalName = appGet('modalActiveName', '');
  if (modalActive && (modalName === 'sizing' || modalName === 'hemming')) {
    notifierDismiss();
  }
  teardown();
}

onPageChange();
document.addEventListener('turbo:load', onPageChange);

log('[Product] registered');
